// src/utils/variantAttributes.ts

export type AttrNew = { name?: string; value?: string | null };
export type AttrOld = { name?: string; values?: any[] };
export type AttrAny = AttrNew | AttrOld;

// supports:
// - [{name, value}]   ✅ new
// - [{name, values[]}] ✅ old
// - { flavor: "x", size: "y" } ✅ object
// - JSON string of any of the above ✅ stringified
export function safeParseAttributes(input: any): any {
  if (!input) return null;
  if (typeof input === "object") return input;
  if (typeof input === "string") {
    try {
      return JSON.parse(input);
    } catch {
      return input;
    }
  }
  return input;
}

export function formatAttributes(input: any, opts?: { sep?: string; empty?: string }) {
  const sep = opts?.sep ?? " / ";
  const empty = opts?.empty ?? "";

  const a = safeParseAttributes(input);
  if (!a) return empty;

  // array shape
  if (Array.isArray(a)) {
    const parts = a
      .map((attr: any) => {
        const name = (attr?.name ?? "").toString().trim();

        // NEW shape: {name, value}
        if (attr && "value" in attr) {
          const v = attr?.value;
          const val = v === null || v === undefined ? "" : String(v).trim();
          if (!val) return "";
          return name ? `${name}: ${val}` : val;
        }

        // OLD shape: {name, values: []}
        if (Array.isArray(attr?.values)) {
          const vals = attr.values.map(String).filter(Boolean).join(", ");
          if (!vals) return "";
          return name ? `${name}: ${vals}` : vals;
        }

        // fallback
        const raw = attr?.values ?? attr?.value ?? "";
        const val = raw === null || raw === undefined ? "" : String(raw).trim();
        if (!val) return "";
        return name ? `${name}: ${val}` : val;
      })
      .filter(Boolean);

    return parts.join(sep) || empty;
  }

  // object shape
  if (typeof a === "object") {
    const parts = Object.entries(a)
      .map(([k, v]) => {
        const val = v === null || v === undefined ? "" : String(v).trim();
        return val ? `${k}: ${val}` : "";
      })
      .filter(Boolean);

    return parts.join(opts?.sep ?? " • ") || empty;
  }

  // plain string/number
  return String(a);
}

export function getAttrValue(input: any, key: string): string {
  const a = safeParseAttributes(input);
  if (!a) return "";

  // array
  if (Array.isArray(a)) {
    const found = a.find((x: any) => String(x?.name || "").toLowerCase() === key.toLowerCase());
    if (!found) return "";

    if ("value" in found) return String(found.value ?? "");
    if (Array.isArray(found.values)) return found.values.map(String).filter(Boolean).join(", ");
    return "";
  }

  // object
  if (typeof a === "object") {
    const v = (a as any)[key];
    return v === null || v === undefined ? "" : String(v);
  }

  return "";
}
